import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { adminApi } from "@/lib/adminApi";
import { Field } from "@/pages/admin/Field";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Pencil, Search } from "lucide-react";

export const DistrictsSection = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = () => api.districts().then(setRows);
  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const openEdit = (dst) => { setForm(dst); setOpen(true); };

  const save = async () => {
    try {
      await adminApi.updateDistrict(form.slug, {
        coordinator_en: form.coordinator_en, coordinator_gu: form.coordinator_gu,
      });
      toast.success("District updated"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const filtered = rows.filter((r) => r.name_en.toLowerCase().includes(q.toLowerCase()) || r.name_gu.includes(q));

  return (
    <div>
      <h2 className="mb-5 font-serif text-2xl font-semibold text-gmoaa-green">Districts ({rows.length})</h2>
      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gmoaa-text-muted" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search districts..." className="border-gmoaa-border-subtle bg-white pl-9" />
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {filtered.map((dst) => (
          <div key={dst.id} className="flex items-center justify-between rounded-xl border border-gmoaa-border-subtle bg-white p-3">
            <div className="min-w-0">
              <p className="font-medium text-gmoaa-text-dark">{dst.name_en} <span className="font-gujarati text-gmoaa-text-muted">· {dst.name_gu}</span></p>
              <p className="text-xs text-gmoaa-text-muted">{dst.member_count ?? 0} members</p>
              <div className="mt-1 flex flex-col gap-0.5 text-xs">
                {dst.president && (
                  <span className="flex items-center gap-1.5 text-gmoaa-text-muted">President: <b className="text-gmoaa-text-dark">{dst.president.name_en}</b>
                    {!dst.president.approved && <span data-testid={`pres-pending-${dst.slug}`} className="rounded bg-gmoaa-red-bg px-1.5 py-0.5 text-[10px] font-semibold text-gmoaa-red">Pending approval</span>}</span>
                )}
                {dst.coordinator && (
                  <span className="flex items-center gap-1.5 text-gmoaa-text-muted">Coordinator: <b className="text-gmoaa-text-dark">{dst.coordinator.name_en}</b>
                    {!dst.coordinator.approved && <span data-testid={`coord-pending-${dst.slug}`} className="rounded bg-gmoaa-red-bg px-1.5 py-0.5 text-[10px] font-semibold text-gmoaa-red">Pending approval</span>}</span>
                )}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => openEdit(dst)}><Pencil size={15} /></Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-gmoaa-cream">
          <DialogHeader><DialogTitle className="text-gmoaa-green">Edit {form.name_en}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Coordinator (English)" value={form.coordinator_en} onChange={set("coordinator_en")} />
            <Field label="Coordinator (Gujarati)" value={form.coordinator_gu} onChange={set("coordinator_gu")} />
            <p className="text-xs text-gmoaa-text-muted">Member counts are computed live from the roster and cannot be edited. Assign District President / Coordinator from Member Roster → Leaders.</p>
          </div>
          <DialogFooter><Button onClick={save} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
